<script setup lang="ts">
import { api } from '@/api';
import { type InstallationConfigDto } from '@/api/generator';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import type { TabDefinition } from '@/components/Tabs/types';
import { stopErrorPropagation } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import InstallationStepActivation from '@/views/InstallationView/components/InstallationStepActivation.vue';
import InstallationStepDatabaseSettings from '@/views/InstallationView/components/InstallationStepDatabaseSettings.vue';
import InstallationStepEmailSettings from '@/views/InstallationView/components/InstallationStepEmailSettings.vue';
import InstallationStepInstallationName from '@/views/InstallationView/components/InstallationStepInstallationName.vue';
import InstallationStepUserAccount from '@/views/InstallationView/components/InstallationStepUserAccount.vue';
import { type CompletedEventArgs, completedStepsOrdered } from '@/views/InstallationView/types';
import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toEmailSettingsEditDto } from './mapping.ts';

const { t } = useTranslation();

const authenticationStore = useAuthenticationStore();
const { session } = storeToRefs(authenticationStore);

const router = useRouter();
const route = useRoute();

const installationConfigDto = ref<InstallationConfigDto>();
const databaseJobIsRunning = ref<boolean>(false);

const completedStep = computed(
  () =>
    completedStepsOrdered.find((step) => step === installationConfigDto.value?.completedStep) ??
    completedStepsOrdered[4],
);

const isDataBaseChanged = ref(false);

const tabs = computed(
  () =>
    ({
      activation: {
        unmountTab: true,
        title: `1. ${t('Activation')}`,
        disabled: !session.value?.isFirstTimeInstallation || databaseJobIsRunning.value,
      },
      databaseSettings: {
        unmountTab: true,
        title: `2. ${t('DatabaseConfiguration')}`,
        disabled:
          !session.value?.isFirstTimeInstallation || !session.value?.isInstallationActivated,
      },
      installationName: {
        unmountTab: true,
        title: `3. ${t('InstallationName')}`,
        disabled:
          !session.value?.isFirstTimeInstallation ||
          !session.value?.isInstallationActivated ||
          databaseJobIsRunning.value ||
          isDataBaseChanged.value ||
          completedStepsOrdered.indexOf(completedStep.value) <= 0,
      },
      emailSettings: {
        unmountTab: true,
        title: `4. ${t('EmailSettings')}`,
        disabled:
          !session.value?.isFirstTimeInstallation ||
          !session.value?.isInstallationActivated ||
          databaseJobIsRunning.value ||
          isDataBaseChanged.value ||
          completedStepsOrdered.indexOf(completedStep.value) <= 1,
      },
      userAccount: {
        unmountTab: true,
        title: `5. ${t('UserAccountAndSignIn')}`,
        disabled:
          databaseJobIsRunning.value ||
          isDataBaseChanged.value ||
          completedStepsOrdered.indexOf(completedStep.value) <= 2,
      },
    }) satisfies TabDefinition,
);

function setOtherStepsAccess(value: boolean) {
  isDataBaseChanged.value = value;
}

// reset the tab disabled state when switching from
// the database settings tab to the other tabs
watch(route, (routeNew) => {
  if (routeNew.hash.startsWith('#tab:')) {
    const tabKey = routeNew.hash.replace('#tab:', '');
    if (tabKey !== 'databaseSettings') {
      setOtherStepsAccess(false);
    }
  }
});

const getInstallationConfiguration = async () => {
  if (session.value?.isFirstTimeInstallation) {
    const { result } = await api.installApi.getInstallationConfig([stopErrorPropagation]);
    installationConfigDto.value = result;
  }
};

onMounted(async () => {
  await getInstallationConfiguration();
});

async function handleCompleted(
  tab: string,
  shouldCheckAuthentication: boolean,
  eventArgs: CompletedEventArgs,
) {
  if (eventArgs.shouldUpdate) {
    await getInstallationConfiguration();
  }
  // we need to update the session to get new values for isFirstTimeInstallation / isInstallationActivated
  // and make sure new XSRF-Token is fetched
  if (shouldCheckAuthentication) {
    await authenticationStore.checkAuthentication();
  }

  // stay on current step
  if (eventArgs.preventNextNavigation) return;

  await router.push({
    name: routenames.installation,
    hash: `#${TAB_PREFIX}${tab}`,
  });
}
</script>

<template>
  <BasePage>
    <template #title>
      <BasePageTitle :title="t('CompleteInstallation')" />
    </template>
    <BasePageNavigation :tabs="tabs" :centered="true">
      <template #activation>
        <InstallationStepActivation
          v-if="installationConfigDto"
          :csoPortalUri="installationConfigDto.csoPortalUrl"
          @completed="handleCompleted('databaseSettings', false, $event)"
        />
      </template>
      <template #databaseSettings>
        <InstallationStepDatabaseSettings
          v-if="installationConfigDto"
          v-model:databaseJobIsRunning="databaseJobIsRunning"
          :defaultConnectionString="installationConfigDto.defaultConnectionString"
          :blobStoringConnectionString="installationConfigDto.blobStoringConnectionString"
          :integratedDefaultConnectionString="
            installationConfigDto.integratedDefaultConnectionString
          "
          :integratedBlobConnectionString="installationConfigDto.integratedBlobConnectionString"
          :isCustomDatabaseUsed="installationConfigDto.isCustomDatabaseUsed"
          :completedStep="completedStep"
          @change="setOtherStepsAccess"
          @completed="handleCompleted('installationName', true, $event)"
        />
      </template>
      <template #installationName>
        <InstallationStepInstallationName
          v-if="installationConfigDto"
          :customerProvidedOptionalSiteName="
            installationConfigDto.customerProvidedOptionalSiteName ?? ''
          "
          :completedStep="completedStep"
          @completed="handleCompleted('emailSettings', false, $event)"
        />
      </template>
      <template #emailSettings>
        <InstallationStepEmailSettings
          v-if="installationConfigDto?.emailSettings"
          :emailSettings="toEmailSettingsEditDto(installationConfigDto.emailSettings)"
          :completedStep="completedStep"
          :settingsAreProven="installationConfigDto.emailSettings.settingsAreProven"
          @completed="handleCompleted('userAccount', false, $event)"
        />
      </template>
      <template #userAccount>
        <InstallationStepUserAccount
          :completedStep="completedStep"
          @completed="handleCompleted('userAccount', true, $event)"
        />
      </template>
    </BasePageNavigation>
  </BasePage>
</template>
